import React, { useState, useContext, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import axios from "../../axiosConfig";
import { TrainerContext } from "../../context/TrainerContext";
import { useTrainerOnboarding } from "../../context/TrainerOnboardingContext";
import { FiHelpCircle } from 'react-icons/fi';
import "../../styles/AccountDetails.css";

const TrainerAccountDetails = () => {
    const { t } = useTranslation(['account', 'common']);
    const { trainer, refreshTrainerData } = useContext(TrainerContext);
    const { resetOnboarding, hasCompletedOnboarding, isUpdating } = useTrainerOnboarding();
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: {
            country: '',
            city: '',
            postalCode: '',
            street: '',
            buildingNumber: '',
            apartmentNumber: ''
        }
    });
    const [isEditing, setIsEditing] = useState(false);
    const [isChangingPassword, setIsChangingPassword] = useState(false);
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: ''
    });

    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [loading, setLoading] = useState(false);

    // Initialize form data when trainer data is loaded
    useEffect(() => {
        if (trainer) {
            setFormData({
                firstName: trainer.firstName || '',
                lastName: trainer.lastName || '',
                email: trainer.email || '',
                phone: trainer.phone || '',
                address: {
                    country: trainer.address?.country || '',
                    city: trainer.address?.city || '',
                    postalCode: trainer.address?.postalCode || '',
                    street: trainer.address?.street || '',
                    buildingNumber: trainer.address?.buildingNumber || '',
                    apartmentNumber: trainer.address?.apartmentNumber || ''
                }
            });
        }
    }, [trainer]);

    if (!trainer) return <div className="trainer-account-tab__loading">{t('common:loading')}</div>;

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleAddressChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevData => ({
            ...prevData,
            address: {
                ...prevData.address,
                [name]: value
            }
        }));
    };

    const handleEditToggle = () => {
        if (isEditing) {
            // Reset form data to original trainer data when canceling
            setFormData({
                firstName: trainer.firstName || '',
                lastName: trainer.lastName || '',
                email: trainer.email || '',
                phone: trainer.phone || '',
                address: {
                    country: trainer.address?.country || '',
                    city: trainer.address?.city || '',
                    postalCode: trainer.address?.postalCode || '',
                    street: trainer.address?.street || '',
                    buildingNumber: trainer.address?.buildingNumber || '',
                    apartmentNumber: trainer.address?.apartmentNumber || ''
                }
            });
            setError('');
            setSuccessMessage('');
        }
        setIsEditing(!isEditing);
    };

    const handleSubmit = async () => {
        setLoading(true);
        setError('');
        setSuccessMessage('');

        // Ensure address object exists and has the right structure
        const updateData = {
            firstName: formData.firstName || '',
            lastName: formData.lastName || '',
            email: formData.email || '',
            phone: formData.phone || '',
            address: {
                country: formData.address?.country || '',
                city: formData.address?.city || '',
                postalCode: formData.address?.postalCode || '',
                street: formData.address?.street || '',
                buildingNumber: formData.address?.buildingNumber || '',
                apartmentNumber: formData.address?.apartmentNumber || ''
            }
        };

        try {
            await axios.put('/api/v1/trainer/update', updateData, {
                headers: { Authorization: `Bearer ${localStorage.getItem('jwt')}` },
            });

            await refreshTrainerData();
            setIsEditing(false);
            setSuccessMessage(t('account:profileUpdated'));
        } catch (err) {
            console.error('Error updating trainer:', err);
            setError(t('account:profileUpdateError'));
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordChange = (e) => {
        setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
    };

    const handleChangePasswordSubmit = async () => {
        setLoading(true);
        setError('');
        setSuccessMessage('');

        if (passwordData.newPassword !== passwordData.confirmNewPassword) {
            setError(t('account:passwordsMismatch'));
            setLoading(false);
            return;
        }

        try {
            await axios.put('/api/v1/auth/change-password', {
                currentPassword: passwordData.currentPassword,
                newPassword: passwordData.newPassword
            }, {
                headers: { Authorization: `Bearer ${localStorage.getItem('jwt')}` },
            });

            setSuccessMessage(t('account:passwordChanged'));
            setPasswordData({ currentPassword: '', newPassword: '', confirmNewPassword: '' });
            setIsChangingPassword(false);
        } catch (err) {
            setError(t('account:passwordChangeError'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="trainer-account-tab">
            {/* Personal Information Section */}
            <div className="trainer-account-tab__section">
                <h3 className="trainer-account-tab__section-title">{t('account:personalInfo')}</h3>

                <div className="trainer-account-tab__row trainer-account-tab__row--personal-info">
                    <div className="trainer-account-tab__field-group">
                        <label className="trainer-account-tab__label">{t('account:firstName')}</label>
                        <input
                            className="trainer-account-tab__input"
                            type="text"
                            name="firstName"
                            value={formData.firstName || ''}
                            onChange={handleChange}
                            disabled={!isEditing}
                            placeholder={t('account:firstName')}
                        />
                    </div>
                    <div className="trainer-account-tab__field-group">
                        <label className="trainer-account-tab__label">{t('account:lastName')}</label>
                        <input
                            className="trainer-account-tab__input"
                            type="text"
                            name="lastName"
                            value={formData.lastName || ''}
                            onChange={handleChange}
                            disabled={!isEditing}
                            placeholder={t('account:lastName')}
                        />
                    </div>
                    <div className="trainer-account-tab__field-group">
                        <label className="trainer-account-tab__label">{t('account:email')}</label>
                        <input
                            className="trainer-account-tab__input"
                            type="email"
                            name="email"
                            value={formData.email || ''}
                            disabled
                            placeholder={t('account:email')}
                        />
                    </div>
                    <div className="trainer-account-tab__field-group">
                        <label className="trainer-account-tab__label">{t('account:phone')}</label>
                        <input
                            className="trainer-account-tab__input"
                            type="text"
                            name="phone"
                            value={formData.phone || ''}
                            onChange={handleChange}
                            disabled={!isEditing}
                            placeholder={t('account:phone')}
                        />
                    </div>
                </div>
            </div>

            {/* Address Section */}
            <div className="trainer-account-tab__section">
                <h3 className="trainer-account-tab__section-title">{t('account:address')}</h3>

                {/* First row: Country, Postal Code, City */}
                <div className="trainer-account-tab__address-row-1">
                    <div className="trainer-account-tab__field-group">
                        <label className="trainer-account-tab__label">{t('account:country')}</label>
                        <input
                            className="trainer-account-tab__input"
                            type="text"
                            name="country"
                            value={formData.address?.country || ''}
                            onChange={handleAddressChange}
                            disabled={!isEditing}
                            placeholder={t('account:country')}
                        />
                    </div>
                    <div className="trainer-account-tab__field-group">
                        <label className="trainer-account-tab__label">{t('account:postalCode')}</label>
                        <input
                            className="trainer-account-tab__input"
                            type="text"
                            name="postalCode"
                            value={formData.address?.postalCode || ''}
                            onChange={handleAddressChange}
                            disabled={!isEditing}
                            placeholder={t('account:postalCode')}
                        />
                    </div>
                    <div className="trainer-account-tab__field-group">
                        <label className="trainer-account-tab__label">{t('account:city')}</label>
                        <input
                            className="trainer-account-tab__input"
                            type="text"
                            name="city"
                            value={formData.address?.city || ''}
                            onChange={handleAddressChange}
                            disabled={!isEditing}
                            placeholder={t('account:city')}
                        />
                    </div>
                </div>

                {/* Second row: Street, Building Number, Apartment Number */}
                <div className="trainer-account-tab__address-row-2">
                    <div className="trainer-account-tab__field-group">
                        <label className="trainer-account-tab__label">{t('account:street')}</label>
                        <input
                            className="trainer-account-tab__input"
                            type="text"
                            name="street"
                            value={formData.address?.street || ''}
                            onChange={handleAddressChange}
                            disabled={!isEditing}
                            placeholder={t('account:street')}
                        />
                    </div>
                    <div className="trainer-account-tab__field-group">
                        <label className="trainer-account-tab__label">{t('account:buildingNumber')}</label>
                        <input
                            className="trainer-account-tab__input"
                            type="text"
                            name="buildingNumber"
                            value={formData.address?.buildingNumber || ''}
                            onChange={handleAddressChange}
                            disabled={!isEditing}
                            placeholder={t('account:buildingNumber')}
                        />
                    </div>
                    <div className="trainer-account-tab__field-group">
                        <label className="trainer-account-tab__label">{t('account:apartmentNumber')}</label>
                        <input
                            className="trainer-account-tab__input"
                            type="text"
                            name="apartmentNumber"
                            value={formData.address?.apartmentNumber || ''}
                            onChange={handleAddressChange}
                            disabled={!isEditing}
                            placeholder={t('account:apartmentNumber')}
                        />
                    </div>
                </div>
            </div>

            {/* Messages */}
            {error && <div className="trainer-account-tab__error-message">{error}</div>}
            {successMessage && <div className="trainer-account-tab__success-message">{successMessage}</div>}

            {/* Action Buttons - Main actions and password change in one row */}
            <div className="trainer-account-tab__button-group">
                <button className="trainer-account-tab__btn trainer-account-tab__btn--secondary" onClick={handleEditToggle}>
                    {isEditing ? t('common:cancel') : t('common:edit')}
                </button>
                {isEditing && (
                    <button className="trainer-account-tab__btn trainer-account-tab__btn--primary" onClick={handleSubmit} disabled={loading}>
                        {loading ? t('common:saving') : t('common:save')}
                    </button>
                )}
                {!isChangingPassword && (
                    <button className="trainer-account-tab__btn trainer-account-tab__btn--outline" onClick={() => setIsChangingPassword(true)}>
                        {t('account:changePasswordButton')}
                    </button>
                )}
            </div>

            {/* Password Change Section */}
            {isChangingPassword && (
                <div className="trainer-account-tab__section trainer-account-tab__password-section">
                    <h3 className="trainer-account-tab__section-title">{t('account:changePassword')}</h3>
                    <div className="trainer-account-tab__row">
                        <div className="trainer-account-tab__field-group">
                            <label className="trainer-account-tab__label">{t('account:currentPassword')}</label>
                            <input
                                className="trainer-account-tab__input"
                                type="password"
                                name="currentPassword"
                                value={passwordData.currentPassword}
                                onChange={handlePasswordChange}
                                placeholder={t('account:currentPassword')}
                            />
                        </div>
                        <div className="trainer-account-tab__field-group">
                            <label className="trainer-account-tab__label">{t('account:newPassword')}</label>
                            <input
                                className="trainer-account-tab__input"
                                type="password"
                                name="newPassword"
                                value={passwordData.newPassword}
                                onChange={handlePasswordChange}
                                placeholder={t('account:newPassword')}
                            />
                        </div>
                        <div className="trainer-account-tab__field-group">
                            <label className="trainer-account-tab__label">{t('account:confirmPassword')}</label>
                            <input
                                className="trainer-account-tab__input"
                                type="password"
                                name="confirmNewPassword"
                                value={passwordData.confirmNewPassword}
                                onChange={handlePasswordChange}
                                placeholder={t('account:confirmPassword')}
                            />
                        </div>
                    </div>
                    <div className="trainer-account-tab__button-group">
                        <button className="trainer-account-tab__btn trainer-account-tab__btn--secondary" onClick={() => setIsChangingPassword(false)}>
                            {t('common:cancel')}
                        </button>
                        <button className="trainer-account-tab__btn trainer-account-tab__btn--primary" onClick={handleChangePasswordSubmit} disabled={loading}>
                            {loading ? t('common:saving') : t('account:savePassword')}
                        </button>
                    </div>
                </div>
            )}

            {/* Onboarding Section */}
            <div className="trainer-account-tab__section trainer-account-tab__onboarding-section">
                <h3 className="trainer-account-tab__section-title">{t('account:onboarding.title')}</h3>
                <div className="trainer-account-tab__onboarding-content">
                    <div className="trainer-account-tab__onboarding-info">
                        <FiHelpCircle className="trainer-account-tab__onboarding-icon" />
                        <div>
                            <p className="trainer-account-tab__onboarding-text">
                                {hasCompletedOnboarding
                                    ? t('account:onboarding.completed')
                                    : t('account:onboarding.notCompleted')}
                            </p>
                        </div>
                    </div>
                    <button
                        className="trainer-account-tab__btn trainer-account-tab__btn--outline trainer-account-tab__onboarding-btn"
                        onClick={resetOnboarding}
                        disabled={isUpdating}
                    >
                        {isUpdating ? t('common:loading') : t('account:onboarding.startGuide')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TrainerAccountDetails;
